import React, { ComponentType } from 'react';
import { Skeleton } from '@progress/kendo-react-indicators';
import * as _ from 'lodash';
import { PureComponent } from '../modules';
import { Position } from '../Types';
import {
  WITH_VIRTUAL_SCROLL_PROPS, 
  WITH_VIRTUAL_SCROLL_STATE
} from './Types';

/**
 * Virtual scroll component for Combo/Component's components
 * @param WrappedComponent
 */

function withVirtualScroll<T extends WITH_VIRTUAL_SCROLL_PROPS>(WrappedComponent: ComponentType<T>) {
  return class extends PureComponent<T, WITH_VIRTUAL_SCROLL_STATE> {
    /**
     * Pending filter timer ref
     * @property {number} filterPending
     */
    filterPending;

    /**
     * Component's data cache
     * @property {any[]} dataCache
     */
    dataCache: any[] = [];

    /**
     * Component's data page cache
     * @property {Set} pageCache
     */
    pageCache = new Set<number>();

    /**
     * Component's requested pages map
     * @property {Object} requestedPages
     */
    requestedPages = {};

    /**
     * Component's reference
     * @property {any} componentRef
     */
    componentRef: any = React.createRef();

    state: WITH_VIRTUAL_SCROLL_STATE = {
      data: [], // dropDown data
      value: null,
      skip: 0, // how many items to skip loading
      total: 0, // total number of items
      filter: '', // remote filter the data
      page: 0 // current page number
    };

    constructor(props: WITH_VIRTUAL_SCROLL_PROPS & T) {
      super(props);

      if (!props.textField) {
        throw new Error('textField is mandatory prop');
      }
      if (!props.dataItemKey) {
        throw new Error('dataItemKey is mandatory prop');
      }
      if (!props.requestConfig) {
        throw new Error('requestConfig is mandatory prop');
      }
    }

    /**
     * Empty item is template item which is rendered
     * before request is finished.
     */
    emptyItem = {
      [this.props.textField]: <Skeleton
        shape={'text'}
        style={{ width: '100%' }}
        animation={{ type: 'wave' }}
      />
    };

    componentDidMount() {
      // trigger one request after cmp is mounted to get first page
      this.requestData(this.state.filter);
    }

    componentWillUnmount() {
      this.resetCache();
    }

    /**
     * Requests data from the server
     * @param {string} filter Filter data parameter
     * @param {number} page
     */
    requestData(filter: string, page = this.state.page) {
      this.requestedPages = { ...this.requestedPages, [page]: true };
      this.dispatch('refreshRemoteList', {
        pos: Position.end,
        stack: true
      }, {
        ...this.props.requestConfig, // url and method
        params: {
          ...this.getParams(page),
          search: (filter && !_.isEmpty(filter)) ? filter : undefined // add search filter if there is search string
        },
        refreshModuleId: this.componentRef.moduleId,
        refreshModuleName: this.componentRef.moduleName
      });
    }
    /**
     * Inserts new items in components dataCache
     * @param {Array} content Data from server, contains name and lastname on array of objects
     * @param {number} page
     */
    cacheNewItems(content: any[], page: number) {
      const { dataItemKey, omitItems } = this.props;
      let dataIndex;
      let cacheItem;
      let itemValue;
      content.forEach((contentItem, index) => {
        dataIndex = page * this.pageSize + index;
        cacheItem = this.dataCache[dataIndex];
        itemValue = dataItemKey ? contentItem[dataItemKey] : contentItem;
        // load new data into cache
        if (!cacheItem || !cacheItem._populated) {
          this.dataCache[dataIndex] = {
            '_index': dataIndex,
            '_populated': true,
            ...contentItem,
            itemDisabled: omitItems?.includes(itemValue)
          };
        }
      });
    }

    /**
     * Sets drop-down data
     * @param {any} data
     */
    setData = (data: any) => {
      const { skip, filter } = this.state;
      const total = data?.totalElements;
      const page = data?.number;

      this.pageCache.add(page);
      this.cacheNewItems(data.content, page);

      // take only data for current page, skip is the last loaded item
      const size = skip + this.pageSize;
      const pageItems = this.dataCache.slice(skip, size).filter(item => item);
      const prevPage = Math.round(skip / this.pageSize);

      if (!this.pageCache.has(prevPage)) {
        this.requestData(filter, prevPage);
      }

      let newState = {};
      // clear the list if nothing is loaded
      if (!data.content.length) {
        newState = {
          total: 0,
          data: []
        };
      } else if (pageItems.length) {
        newState = {
          total: total,
          data: [...pageItems]
        };
      }
      this.setState({
        ...newState
      });
    }

    /**
     * Returns cached data from the last loaded item
     * @param {number} skip The number of items to skip
     */
    getCachedData(skip: number): any[] {
      const data: any[] = [];
      for (let i = 0; i < this.pageSize; i++) {
        data.push(this.dataCache[i + skip] || {
          '_index': i + skip,
          '_populated': false,
          ...this.emptyItem
        });
      }
      return data;
    }

    /**
     * Resets cached data
     */
    resetCache() {
      this.dataCache.length = 0;
      this.pageCache.clear();
      this.requestedPages = {};
    }

    /**
     * Resets filtering, load only one empty item before request
     * @params {String} filter Filter data parameter
     */
    resetFilter(filter) {
      this.setState({
        data: [{
          '_index': 0,
          '_populated': false,
          ...this.emptyItem
        }],
        page: 0,
        skip: 0,
        filter
      });
    }

    /**
     * Triggers filter request
     * @param {any} event
     */
    onFilterChange = (event: any) => {
      let filter = event.filter.value;
      if (this.props.trimSpace) {
        filter = filter.trim();
      }

      clearTimeout(this.filterPending);
      this.filterPending = setTimeout(() => {
        this.resetCache();
        this.resetFilter(filter);
        this.requestData(filter, 0);
      }, 500);
    }

    /**
     * Returns request params
     * @param page
     */
    getParams = (page: number) => {
      const { pageSize = 20, pageSizeName = 'size', pageNumberName = 'page', requestConfig = {} } = this.props;

      return {
        [pageNumberName]: page || this.state.page || 0,
        [pageSizeName]: pageSize,
        ...(requestConfig?.params || {})
      };
    };

    /**
     * Returns the page number
     * @param {number} skip The number of items to skip from cache
     */
    getPage = (skip: number) => Math.ceil(skip / this.pageSize);

    /**
     * Get data for current page
     * @param {any} event
     */
    pageChange = (event: any) => {
      const { skip } = event.page ?? event;
      const filter = this.state.filter;
      let page = this.getPage(skip);

      this.setState({ page, skip });
      if (this.shouldRequestData(skip) && !this.requestedPages[page]) {
        this.requestData(filter, page);
      }

      let data = this.getCachedData(skip);
      this.setState({
        data: [...data]
      });
    }

    /**
     * Returns true if page data should be requested
     * @param {number} skip The number of items to skip
     */
    shouldRequestData(skip: number) {
      for (let i = 0; i < this.pageSize; i++) {
        if (!this.dataCache[skip + i]) {
          return true;
        }
      }
      return false;
    }

    /**
     * Saves state value
     * @param event
     */
    onChange = (event: any) => {
      const { textField } = this.props;
      // any change from event should be read from e.value because e.target.value is readonly
      // and isn't necessarily in sync with state
      const value = event.value;
      if (value && value[textField] === this.emptyItem[textField]) {
        return;
      }

      // the onChange is propagated here to avoid getting emptyItem as value while the list is waiting for data
      this.props?.onChange?.(event);
    }

    /**
     * Returns page size
     * @returns {number}
     */
    get pageSize() {
      return this.props.pageSize || 20;
    }

    render() {
      const { data, skip, total } = this.state;

      return (
        <WrappedComponent
          {...this.props as T & WITH_VIRTUAL_SCROLL_PROPS}
          onChange={this.onChange}
          data={data}
          ref={this.refCallback}
          setData={this.setData}
          onPageChange={this.pageChange}
          onFilterChange={this.onFilterChange}
          virtual={{
            pageSize: this.pageSize,
            skip: skip,
            total: total
          }}
        />
      );
    }

    /**
     * Reference callback
     */
    refCallback = (ref: any) => {
      ref = ref && ref.componentRef ? ref.componentRef : ref;
      this.componentRef = ref;
    };
  };
}

export default withVirtualScroll;
